import FinalFormField from 'common/components/final-form/FinalFormField';
import useCodeTable from 'common/hooks/useCodeTable';
import ActionStatus from 'common/types/ActionStatus';
import FormVerificationResponse from 'domain/components/verify-identity/FormVerificationResponse';
import React from 'react';
import {
  Col,
  FormFile,
  FormGroup,
  FormLabel,
  FormText,
  Row,
} from 'react-bootstrap';
import ResidenceDisplay from './ResidenceDisplay';
import ResidenceStatus from './ResidenceStatus';
import AcceptableDocsLink from 'common/components/links/AcceptableDocsLink';
import { Form } from 'react-final-form';
import ChsAttachment from 'domain/types/ChsAttachment';
import Alert from 'common/components/alert/Alert';
import AlertType from 'common/types/AlertType';
import { getAttachment } from './upload';
import { CodeTableEntry } from '../../codetable/CodeTableOptions';

interface IResidenceUpload {
  showValidations: boolean;
  verification: FormVerificationResponse | null;
  attachment1?: File | null; // attachment when there is no existing application attachment.
  attachment2?: File | null; // attachment when there is no existing application attachment.
  uploadStatus: ActionStatus;
  uploadMessage: string;
  onDocument1Change: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDocument2Change: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAttachmentType1Change: (value: string) => void;
  onAttachmentType2Change: (value: string) => void;
  error?: string;
  filesLoaded?: boolean;
  colSize?: number;
  hideExistingDisplay?: boolean;
}

interface AttachmentFieldProps {
  id: string;
  label: string;
  selectId: string;
  attachment: ChsAttachment | null;
  newAttachment?: File | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  options: CodeTableEntry[];
}

const UploadButton = ({ id }: { id: string }) => (
  <label htmlFor={id} className='btn btn-link pl-0'>
    Select file
  </label>
);

const ResidenceUpload = (props: IResidenceUpload) => {
  const {
    onDocument1Change,
    onDocument2Change,
    onAttachmentType1Change,
    onAttachmentType2Change,
    filesLoaded,
    uploadStatus,
    uploadMessage,
    verification,
    hideExistingDisplay,
    showValidations,
    error,
    attachment1,
    attachment2,
  } = props;

  const options: CodeTableEntry[] = useCodeTable({
    includeEmpty: true,
    endpoint: '/primaryResidencyAttachments',
  });

  const hasAttachments =
    verification && verification.primaryResAttachment.length >= 2;

  const validatedFile1 = getAttachment(verification, 'PRIMARY_RESIDENCE');
  const validatedFile2 = getAttachment(verification, 'PRIMARY_RESIDENCE_02');

  const AttachmentField = ({
    id,
    label,
    selectId,
    attachment,
    newAttachment,
    onChange,
    options,
  }: AttachmentFieldProps) => {
    const isApproved =
      (attachment && attachment.statusCd === 'APPROVED') || false;
    const isRejected =
      (attachment && attachment.statusCd === 'REJECTED') || false;

    return (
      <FormGroup as={Row}>
        <Col sm={{ span: props.colSize || 6 }} className='align-self-center'>
          {isRejected && (
            <Alert type={AlertType.danger}>
              Please re-upload Primary Residency Document.
            </Alert>
          )}
          {isApproved && (
            <Alert type={AlertType.success}>This document is approved.</Alert>
          )}
          <>
            <p>
              <strong>{label}</strong>
            </p>
            <FormFile
              type='file'
              id={id}
              onChange={(e: any) => {
                onChange(e);
              }}
              className='d-none'
            />
            {!isApproved && <UploadButton id={id} />}
            {attachment && (isApproved || (!isApproved && !newAttachment)) && (
              <p>{attachment.filename}</p>
            )}
            {newAttachment && <p>{newAttachment.name}</p>}
            <FinalFormField
              type='select'
              name={selectId}
              label='Document Type'
              disabled={uploadStatus === ActionStatus.saving || isApproved}
              text='All documentation must be current/unexpired.'
              options={options}
            />
          </>
        </Col>
      </FormGroup>
    );
  };

  return (
    <>
      {verification && (
        <ResidenceStatus
          hasAttachment={Boolean(hasAttachments || filesLoaded)}
          showValidation={showValidations}
          status={verification.status}
          uploadStatus={uploadStatus}
          uploadMessage={uploadMessage}
          error={error}
        />
      )}

      {/* If verification status is null, INITIAL, PENDING, or REJECTED, allow re-upload. */}
      {verification && verification.status !== 'APPROVED' && (
        <Form
          initialValues={{
            attachmentTypeCd1: hasAttachments
              ? validatedFile1
                ? validatedFile1.attachmentTypeCd
                : null
              : null,
            attachmentTypeCd2: hasAttachments
              ? validatedFile2
                ? validatedFile2.attachmentTypeCd
                : null
              : null,
          }}
          onSubmit={(values) => {
            onAttachmentType1Change(values.attachmentTypeCd1);
            onAttachmentType2Change(values.attachmentTypeCd2);
          }}
        >
          {({ handleSubmit, values }) => (
            <form onChange={handleSubmit}>
              <FormGroup as={Row}>
                <Col
                  sm={{ span: props.colSize || 6 }}
                  className='align-self-center'
                >
                  <FormLabel htmlFor='files'>
                    Please upload <u>2 documents</u> from different categories
                    on the{' '}
                    <AcceptableDocsLink>approved list</AcceptableDocsLink>{' '}
                    showing proof of primary residency.*
                  </FormLabel>
                  {verification.allowedFileTypes && (
                    <FormText className='text-muted'>
                      Allowed File Types: {verification.allowedFileTypes}
                    </FormText>
                  )}
                  <FormText className='text-muted'>
                    Unit number must be listed on the uploaded document unless
                    residence is single-family.
                  </FormText>
                </Col>
              </FormGroup>

              <AttachmentField
                id='files-1'
                label="Primary Residency: ID or Driver's License "
                onChange={onDocument1Change}
                selectId='attachmentTypeCd1'
                attachment={validatedFile1}
                newAttachment={attachment1}
                options={options.filter(
                  (c) => c.cd === 'LICENSE_OR_ID' || c.cd === ''
                )}
              />

              <AttachmentField
                id='files-2'
                label='Primary Residency: Supplementary Document'
                onChange={onDocument2Change}
                selectId='attachmentTypeCd2'
                attachment={validatedFile2}
                newAttachment={attachment2}
                options={options.filter((c) => c.cd !== 'LICENSE_OR_ID')}
              />

              <FormGroup as={Row}>
                <Col
                  sm={{ span: props.colSize || 6 }}
                  className='align-self-center'
                >
                  {values.attachmentTypeCd1 &&
                    values.attachmentTypeCd2 &&
                    values.attachmentTypeCd1 === values.attachmentTypeCd2 && (
                      <div className={`d-block invalid-feedback`}>
                        Document types must be different.
                      </div>
                    )}
                </Col>
              </FormGroup>
            </form>
          )}
        </Form>
      )}
      {hasAttachments && hideExistingDisplay !== true && (
        // If attachment name and type available, show
        <ResidenceDisplay
          files={[
            {
              name: validatedFile1?.filename || '',
              description: validatedFile1?.attachmentTypeDescr || '',
            },
            {
              name: validatedFile2?.filename || '',
              description: validatedFile2?.attachmentTypeDescr || '',
            },
          ]}
        />
      )}
    </>
  );
};

export default ResidenceUpload;
